/**
 * Genre-neutral danger-zone geometry (circle / cone / line).
 * Feature owns emitters, skill kind taxonomy, and Soft≪AA phen labels.
 * Phaser-free.
 */

export type DangerZoneShape = "circle" | "cone" | "line";

/** Minimal volume for hit / clear / project queries. */
export type DangerZoneGeom = {
  shape: DangerZoneShape;
  x: number;
  y: number;
  range: number;
  coneDeg?: number;
  aimAngle?: number;
  /** Line corridor half-width (world px). */
  halfWidth?: number;
  /** Optional severity/filter key (feature-defined). */
  danger?: string;
};

function angleDiff(a: number, b: number): number {
  let d = a - b;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return d;
}

/** Hit-aligned feet test: circle/cone/line expanded by unitRadius. */
export function pointInDangerZone(
  x: number,
  y: number,
  unitRadius: number,
  z: DangerZoneGeom,
): boolean {
  switch (z.shape) {
    case "circle": {
      const d = Math.hypot(x - z.x, y - z.y);
      return d <= z.range + unitRadius;
    }
    case "cone": {
      const d = Math.hypot(x - z.x, y - z.y);
      if (d > z.range + unitRadius) return false;
      const ang = Math.atan2(y - z.y, x - z.x);
      const aim = z.aimAngle ?? -Math.PI / 2;
      const half = ((z.coneDeg ?? 60) * Math.PI) / 180 / 2;
      const bodyAng =
        unitRadius > 0 && d > 1 ? Math.asin(Math.min(1, unitRadius / d)) : 0;
      return Math.abs(angleDiff(ang, aim)) <= half + bodyAng;
    }
    case "line": {
      const aim = z.aimAngle ?? 0;
      const len = Math.max(1e-3, z.range);
      const hw = Math.max(1, z.halfWidth ?? z.coneDeg ?? 20) + unitRadius;
      const dx = x - z.x;
      const dy = y - z.y;
      const fx = Math.cos(aim);
      const fy = Math.sin(aim);
      const along = dx * fx + dy * fy;
      if (along < -unitRadius || along > len + unitRadius) return false;
      const px = dx - fx * along;
      const py = dy - fy * along;
      return Math.hypot(px, py) <= hw;
    }
    default: {
      const _exhaustive: never = z.shape;
      return _exhaustive;
    }
  }
}

export function pointInAnyDangerZone(
  x: number,
  y: number,
  unitRadius: number,
  zones: readonly DangerZoneGeom[],
  danger?: string,
): boolean {
  for (const z of zones) {
    if (danger && z.danger !== danger) continue;
    if (pointInDangerZone(x, y, unitRadius, z)) return true;
  }
  return false;
}

/** True when (x,y) clears the zone hit volume by at least `pad` (+ body). */
export function pointClearsDangerZone(
  x: number,
  y: number,
  unitRadius: number,
  z: DangerZoneGeom,
  pad: number,
): boolean {
  switch (z.shape) {
    case "circle": {
      const d = Math.hypot(x - z.x, y - z.y);
      return d >= z.range + unitRadius + pad;
    }
    case "cone": {
      const d = Math.hypot(x - z.x, y - z.y);
      if (d >= z.range + unitRadius + pad) return true;
      const ang = Math.atan2(y - z.y, x - z.x);
      const aim = z.aimAngle ?? -Math.PI / 2;
      const half = ((z.coneDeg ?? 60) * Math.PI) / 180 / 2;
      const ring = Math.max(d, 1);
      const bodyAng = Math.asin(Math.min(1, (unitRadius + pad) / ring));
      return Math.abs(angleDiff(ang, aim)) > half + bodyAng;
    }
    case "line": {
      const aim = z.aimAngle ?? 0;
      const len = Math.max(1e-3, z.range);
      const hw = Math.max(1, z.halfWidth ?? z.coneDeg ?? 20) + unitRadius + pad;
      const dx = x - z.x;
      const dy = y - z.y;
      const fx = Math.cos(aim);
      const fy = Math.sin(aim);
      const along = dx * fx + dy * fy;
      if (along < -(unitRadius + pad) || along > len + unitRadius + pad) {
        return true;
      }
      const px = dx - fx * along;
      const py = dy - fy * along;
      return Math.hypot(px, py) > hw;
    }
    default: {
      const _exhaustive: never = z.shape;
      return _exhaustive;
    }
  }
}

export function pointClearsAllDangerZones(
  x: number,
  y: number,
  unitRadius: number,
  zones: readonly DangerZoneGeom[],
  pad: number,
  danger?: string,
): boolean {
  for (const z of zones) {
    if (danger && z.danger !== danger) continue;
    if (!pointClearsDangerZone(x, y, unitRadius, z, pad)) return false;
  }
  return true;
}

/**
 * Push a candidate XY out of every active zone (pad clear).
 * Shared by dodge goals / formation slots.
 */
export function projectClearOfDangerZones(
  x0: number,
  y0: number,
  unitRadius: number,
  zones: readonly DangerZoneGeom[],
  pad = 4,
  danger?: string,
): { x: number; y: number } {
  let x = x0;
  let y = y0;
  if (zones.length === 0) return { x, y };
  if (pointClearsAllDangerZones(x, y, unitRadius, zones, pad, danger)) {
    return { x, y };
  }
  for (let iter = 0; iter < 12; iter++) {
    let pushed = false;
    for (const z of zones) {
      if (danger && z.danger !== danger) continue;
      if (pointClearsDangerZone(x, y, unitRadius, z, pad)) continue;
      pushed = true;
      if (z.shape === "circle") {
        const dx = x - z.x;
        const dy = y - z.y;
        const d = Math.hypot(dx, dy) || 1;
        const need = z.range + unitRadius + pad - d;
        if (need > 0) {
          x += (dx / d) * (need + 1);
          y += (dy / d) * (need + 1);
        }
      } else if (z.shape === "line") {
        const aim = z.aimAngle ?? 0;
        if (!Number.isFinite(aim)) continue;
        const fx = Math.cos(aim);
        const fy = Math.sin(aim);
        const px = -fy;
        const py = fx;
        const dx = x - z.x;
        const dy = y - z.y;
        const lat = dx * px + dy * py;
        const side = lat >= 0 || Math.abs(lat) < 1e-3 ? 1 : -1;
        const hw = Math.max(1, z.halfWidth ?? 20) + unitRadius + pad;
        const need = hw - Math.abs(lat);
        if (need > 0) {
          x += px * side * (need + 1);
          y += py * side * (need + 1);
        }
      } else {
        const aim = z.aimAngle ?? -Math.PI / 2;
        const px = -Math.sin(aim);
        const py = Math.cos(aim);
        const ang = Math.atan2(y - z.y, x - z.x);
        let dAng = ang - aim;
        if (!Number.isFinite(dAng)) dAng = 0;
        while (dAng > Math.PI) dAng -= Math.PI * 2;
        while (dAng < -Math.PI) dAng += Math.PI * 2;
        const side = dAng >= 0 ? 1 : -1;
        x += px * side * Math.max(18, pad + unitRadius);
        y += py * side * Math.max(18, pad + unitRadius);
      }
    }
    if (!pushed) break;
    if (pointClearsAllDangerZones(x, y, unitRadius, zones, pad, danger)) break;
  }
  return { x, y };
}

/**
 * First zone the segment enters that feet are not already standing in.
 * Skips t=0 so in-zone exits are not treated as illegal transit.
 */
export function firstEnteredDangerZoneOnSegment<T extends DangerZoneGeom>(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  unitRadius: number,
  zones: readonly T[],
  steps = 8,
  danger?: string,
): T | null {
  if (zones.length === 0) return null;
  const n = Math.max(2, Math.floor(steps));
  for (let i = 1; i <= n; i++) {
    const t = i / n;
    const x = x0 + (x1 - x0) * t;
    const y = y0 + (y1 - y0) * t;
    for (const z of zones) {
      if (danger && z.danger !== danger) continue;
      if (!pointInDangerZone(x, y, unitRadius, z)) continue;
      if (pointInDangerZone(x0, y0, unitRadius, z)) continue;
      return z;
    }
  }
  return null;
}

/** True when the path newly enters any zone (not already standing in). */
export function segmentEntersDangerZone(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  unitRadius: number,
  zones: readonly DangerZoneGeom[],
  steps = 8,
  danger?: string,
): boolean {
  return (
    firstEnteredDangerZoneOnSegment(
      x0,
      y0,
      x1,
      y1,
      unitRadius,
      zones,
      steps,
      danger,
    ) != null
  );
}

/** First zone containing the point (optional danger filter). */
export function activeDangerZoneAt<T extends DangerZoneGeom>(
  x: number,
  y: number,
  unitRadius: number,
  zones: readonly T[],
  danger?: string,
): T | null {
  for (const z of zones) {
    if (danger && z.danger !== danger) continue;
    if (pointInDangerZone(x, y, unitRadius, z)) return z;
  }
  return null;
}
